import { useCallback, useEffect, useRef, useState } from "react";

const FAST_MS = 60;
const IDLE_MS = 250;
const RESET_GAP_MS = 1500;

const isEditable = (el) =>
  el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
const inDialog = (el) => Boolean(el?.closest?.('[role="dialog"],[role="alertdialog"],[role="listbox"]'));

// USB NFC readers act like a keyboard: they type the tag ID in a fast burst, usually followed by Enter.
export const useReaderLine = (onScan, { enabled = true, captureGlobal = false } = {}) => {
  const inputRef = useRef(null);
  const valueRef = useRef("");
  const times = useRef([]);
  const idle = useRef(null);
  const cb = useRef(onScan);
  cb.current = onScan;
  const [value, setValueState] = useState("");
  const [keys, setKeys] = useState(0);
  const [lastKeyAt, setLastKeyAt] = useState(null);
  const [docFocus, setDocFocus] = useState(true);

  const setValue = useCallback((v) => {
    valueRef.current = v;
    setValueState(v);
  }, []);

  const submit = useCallback(() => {
    clearTimeout(idle.current);
    const uid = valueRef.current.trim();
    setValue("");
    times.current = [];
    window.dispatchEvent(new Event("reader-submit"));
    if (uid.length >= 3) cb.current(uid);
  }, [setValue]);

  // Operator shortcuts (F, R) fire this so a lone keypress never lingers in the tag buffer.
  useEffect(() => {
    const clear = () => { clearTimeout(idle.current); times.current = []; setValue(""); };
    window.addEventListener("reader-clear", clear);
    return () => window.removeEventListener("reader-clear", clear);
  }, [setValue]);

  const pushChar = useCallback((ch) => {
    const now = performance.now();
    const t = times.current;
    let base = valueRef.current;
    if (t.length && now - t[t.length - 1] > RESET_GAP_MS) {
      t.length = 0;
      base = "";
    }
    t.push(now);
    setValue(base + ch);
    setKeys((k) => k + 1);
    setLastKeyAt(Date.now());
    clearTimeout(idle.current);
    // Reader-speed burst with no Enter suffix: submit once the burst stops.
    const fast = t.length >= 4 && (t[t.length - 1] - t[0]) / (t.length - 1) < FAST_MS;
    if (fast) idle.current = setTimeout(submit, IDLE_MS);
  }, [setValue, submit]);

  const handleKey = useCallback((e) => {
    if (!enabled || e.key === "`") return;
    if ((e.key === "Enter" || e.key === "Tab") && valueRef.current) {
      e.preventDefault();
      submit();
      return;
    }
    if (e.key === "Backspace" && valueRef.current) {
      e.preventDefault();
      setValue(valueRef.current.slice(0, -1));
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      pushChar(e.key);
    }
  }, [enabled, submit, pushChar, setValue]);

  useEffect(() => {
    if (!captureGlobal || !enabled) return undefined;
    const onKey = (e) => {
      const t = e.target;
      if (t === inputRef.current || isEditable(t) || inDialog(t)) return;
      if (e.key.length !== 1 && !valueRef.current) return;
      handleKey(e);
      inputRef.current?.focus({ preventScroll: true });
    };
    const refocus = () => {
      setDocFocus(document.hasFocus());
      const a = document.activeElement;
      if (!a || a === document.body) inputRef.current?.focus({ preventScroll: true });
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("focus", refocus);
    const iv = setInterval(refocus, 1000);
    refocus();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("focus", refocus);
      clearInterval(iv);
    };
  }, [captureGlobal, enabled, handleKey]);

  useEffect(() => () => clearTimeout(idle.current), []);

  return {
    keys,
    lastKeyAt,
    docFocus,
    inputProps: {
      ref: inputRef,
      value,
      onKeyDown: handleKey,
      onChange: (e) => setValue(e.target.value),
      readOnly: !enabled,
    },
  };
};
